import { SiWhatsapp } from "react-icons/si";
import { BRAND } from "@/lib/brand-config";
import { cn } from "@/lib/utils";
import { analytics } from "@/lib/analytics";

// Task #2036 — Single source of truth for the site-wide "Chat on
// WhatsApp" entry points. Every pre-filled message lives here so the
// compliant wording (visa-leaning, educational, "indication of
// interest" tone — never an offer, never "investment/investing/
// invest/returns") stays consistent and reviewable in one place.

export type WhatsAppAudience = "visa-prospect" | "partner" | "general";

const AUDIENCE_MESSAGES: Record<WhatsAppAudience, string> = {
  // Public / prospect pages lean into the EB-5 visa program.
  "visa-prospect": "Hi, I'd like to learn more about the EB-5 visa program.",
  // Partner / referral recruitment pages.
  partner:
    "Hi, I'd like to learn more about the Veras Capital partner and referral program.",
  // General fallback for everything else.
  general:
    "Hi, I have a question about the EB-5 visa program and would like to learn more.",
};

export function whatsAppMessageForAudience(audience: WhatsAppAudience): string {
  return AUDIENCE_MESSAGES[audience] ?? AUDIENCE_MESSAGES.general;
}

// Project-context message references the project by name so the team
// knows where the conversation started — with no investing, return, or
// offer language.
export function whatsAppMessageForProject(projectName: string): string {
  const name = (projectName || "").trim();
  return name
    ? `Hi, I'd like to learn more about ${name} and the EB-5 visa program.`
    : AUDIENCE_MESSAGES["visa-prospect"];
}

// Empty config number is the site-wide kill switch — every WhatsApp
// entry point returns null when there's no number to chat to.
export function isWhatsAppEnabled(): boolean {
  return BRAND.whatsappNumber.trim().length > 0;
}

export function buildWhatsAppUrl(message: string): string {
  const number = BRAND.whatsappNumber.replace(/\D/g, "");
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

interface WhatsAppButtonProps {
  /** Pre-filled message body. Use the preset helpers above to stay compliant. */
  message: string;
  label?: string;
  className?: string;
  testId?: string;
  /** Lightweight analytics label, e.g. "contact-page" or "project-detail". */
  source?: string;
}

/**
 * Inline / contextual "Chat on WhatsApp" link. Opens WhatsApp (app on
 * mobile, WhatsApp Web on desktop) in a new tab, addressed to the
 * business number with a compliant pre-filled message. Renders nothing
 * when no WhatsApp number is configured.
 */
export function WhatsAppButton({
  message,
  label = "Chat on WhatsApp",
  className,
  testId = "button-whatsapp-chat",
  source,
}: WhatsAppButtonProps) {
  if (!isWhatsAppEnabled()) return null;
  return (
    <a
      href={buildWhatsAppUrl(message)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        analytics.track("whatsapp_click", {
          source: source ?? "inline",
        })
      }
      aria-label={label}
      data-testid={testId}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1ebe5b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2",
        className,
      )}
    >
      <SiWhatsapp className="h-4 w-4" aria-hidden="true" />
      {label}
    </a>
  );
}

/**
 * Floating site-wide WhatsApp entry point. A green pill in the bottom
 * corner that opens WhatsApp directly in one click (app on mobile,
 * WhatsApp Web on desktop) with an audience-appropriate pre-filled
 * message. Renders nothing when no number is configured. Mounted only in
 * the public app shell — it is intentionally absent from authenticated
 * portals that already have an in-app inbox.
 */
export function WhatsAppFloatingButton({
  audience,
  aboveStickyBar = false,
  compact = false,
}: {
  audience: WhatsAppAudience;
  /**
   * Collapse to an icon-only circle at every breakpoint. Used on pages
   * whose primary actions run the full width of a bottom-right content
   * column (the projects index cards), where the labelled pill would
   * otherwise sit over a call-to-action as the visitor scrolls.
   */
  compact?: boolean;
  /**
   * Set to true when a sticky bottom bar (e.g. the project-detail CTA
   * bar) is present on the page, so the bubble is lifted above it
   * instead of overlapping it. Accounts for the bar height at each
   * breakpoint and the iOS safe-area inset.
   */
  aboveStickyBar?: boolean;
}) {
  if (!isWhatsAppEnabled()) return null;

  const message = whatsAppMessageForAudience(audience);

  // When a sticky CTA bar is visible at the bottom, lift the bubble
  // above it. The bar is ~64px (4rem) on mobile and ~56px (3.5rem) on
  // sm+, plus env(safe-area-inset-bottom). Using 5rem as the base
  // comfortably clears both breakpoints, and the safe-area term
  // handles the iPhone home-indicator gap that the bar itself already
  // accounts for.
  const bottomStyle = aboveStickyBar
    ? { bottom: "calc(5rem + env(safe-area-inset-bottom))" }
    : undefined;

  return (
    <div
      className={cn(
        "fixed right-2 z-40 flex flex-col items-end gap-3 print:hidden sm:right-4",
        aboveStickyBar ? "" : "bottom-4",
      )}
      style={bottomStyle}
      data-testid="whatsapp-floating"
    >
      <a
        href={buildWhatsAppUrl(message)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          analytics.track("whatsapp_click", {
            source: `floating:${audience}`,
          })
        }
        aria-label="Chat on WhatsApp"
        data-testid="button-whatsapp-fab"
        className={cn(
          "inline-flex items-center gap-2 rounded-full bg-[#25D366] p-2.5 font-semibold text-white shadow-lg transition-colors hover:bg-[#1ebe5b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2",
          compact ? "" : "sm:py-3 sm:pl-3 sm:pr-4",
        )}
      >
        <SiWhatsapp className="h-5 w-5" aria-hidden="true" />
        {!compact && <span className="hidden text-sm sm:inline">Chat on WhatsApp</span>}
      </a>
    </div>
  );
}
